import {createApi, fetchBaseQuery} from "@reduxjs/toolkit/query/react";

// RTK Query API for the DIB admin API, proxied by the BFF under /dibapi
// (YARP strips the prefix and attaches the bearer token server-side).
//
// The PublicNews v2 endpoints below are slated to move to dibAppApi once the
// dib app API serves them — keep them self-contained (no imports from
// dibAppApi) so migration is a copy of the endpoint defs + types.

/** One published news article from GET /dibapi/api/PublicNews/v2/news. */
export type PublicNewsArticleV2 = {
    topicId: string;
    title: string | null;
    description: string | null;
    /** Original article URL for external news; app.dib.no/nyhet/... for dib news. */
    link: string | null;
    /** ISO 8601; null when the topic has no dates at all. */
    pubDate: string | null;
    source: string | null;
    /** Database (product) ids the article is tagged with, ascending. */
    databaseIds: number[] | null;
};

/** Page envelope: total counts all matches after filtering, before paging. */
export type GetNewsResponse = {
    total: number;
    items: PublicNewsArticleV2[];
};

export type NewsFilters = {
    sources?: string[];
    databaseIds?: number[];
    search?: string;
};

export type GetNewsArgs = NewsFilters & {
    skip?: number;
    /** Default 50 server-side, capped at 500. */
    take?: number;
};

// fetchBaseQuery's `params` option comma-joins arrays ("sources=A,B"), but
// ASP.NET binds string[] from repeated params ("sources=A&sources=B") — so
// the query string is built by hand.
function newsQueryString({
    sources,
    databaseIds,
    search,
    skip,
    take,
}: GetNewsArgs) {
    const params = new URLSearchParams();
    for (const source of sources ?? []) params.append("sources", source);
    for (const id of databaseIds ?? []) params.append("databaseIds", String(id));
    if (search) params.set("search", search);
    if (skip) params.set("skip", String(skip));
    if (take != null) params.set("take", String(take));
    const qs = params.toString();
    return qs ? `?${qs}` : "";
}

export const dibAdminApi = createApi({
    reducerPath: "dibAdminApi",
    baseQuery: fetchBaseQuery({baseUrl: "/dibapi"}),
    endpoints: (builder) => ({
        getNews: builder.query<GetNewsResponse, GetNewsArgs>({
            query: (args) => `/api/PublicNews/v2/news${newsQueryString(args)}`,
            // One cache entry per filter set; "load more" pages accumulate
            // into it via merge instead of creating an entry per page.
            serializeQueryArgs: ({endpointName, queryArgs}) => {
                const {skip: _skip, take: _take, ...filters} = queryArgs;
                return `${endpointName}(${JSON.stringify(filters)})`;
            },
            merge: (current, incoming, {arg}) => {
                if (!arg.skip) return incoming;
                current.items.push(...incoming.items);
                current.total = incoming.total;
            },
            forceRefetch: ({currentArg, previousArg}) =>
                currentArg?.skip !== previousArg?.skip,
            keepUnusedDataFor: 300,
        }),
        // There is no by-id endpoint; the list's title search narrows to a
        // handful of rows, and the topicId picks the exact one. null when the
        // topic isn't (or no longer is) in the news feed.
        getNewsItem: builder.query<
            PublicNewsArticleV2 | null,
            {topicId: string; title: string}
        >({
            query: ({title}) =>
                `/api/PublicNews/v2/news${newsQueryString({search: title, take: 10})}`,
            transformResponse: (res: GetNewsResponse, _meta, {topicId}) =>
                res.items.find((item) => item.topicId === topicId) ?? null,
            keepUnusedDataFor: 3600,
        }),
        getNewsDatabaseIds: builder.query<
            number[],
            Omit<NewsFilters, "databaseIds">
        >({
            query: (args) =>
                `/api/PublicNews/v2/databaseIds${newsQueryString(args)}`,
            keepUnusedDataFor: 3600,
        }),
    }),
});
